import React, { createContext, useState, useCallback } from "react";
import axios from "../utils/axios";

export const GlobalContext = createContext();

const hasRequiredRole = (roles, requiredRole) => {
  if (!Array.isArray(roles)) {
    return false;
  }

  return roles.some((role) => String(role).toLowerCase() === requiredRole.toLowerCase());
};

const getValue = (data = {}, ...keys) => {
  const key = keys.find((candidateKey) => data[candidateKey] !== undefined && data[candidateKey] !== null);
  return key ? data[key] : undefined;
};

const normalizeArray = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (value) {
    return [value];
  }

  return [];
};

const normalizeUserInformation = (data = {}) => ({
  id: getValue(data, "id", "Id"),
  firstname: getValue(data, "firstname", "firstName", "Firstname", "FirstName") || "",
  lastname: getValue(data, "lastname", "lastName", "Lastname", "LastName") || "",
  userName: getValue(data, "userName", "UserName") || "",
  email: getValue(data, "email", "Email") || "",
  roles: normalizeArray(getValue(data, "roles", "Roles")),
  allowedCompanies: normalizeArray(getValue(data, "allowedCompanies", "AllowedCompanies")),
  isAccountManager: getValue(data, "isAccountManager", "IsAccountManager"),
});

const normalizeCompanyInformation = (data = {}) => ({
  name: getValue(data, "name", "Name") || "",
  entityId: getValue(data, "entityId", "EntityId"),
  entityNumber: getValue(data, "entityNumber", "EntityNumber"),
});

const GlobalProvider = ({ children }) => {
  const [userId, setUserId] = useState(null);
  const [displayName, setDisplayName] = useState(null);
  const [email, setEmail] = useState(null);
  const [roles, setRoles] = useState([]);
  const [allowedCompanies, setAllowedCompanies] = useState([]);
  const [userName, setUserName] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [companyName, setCompanyName] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const [customerNumber, setCustomerNumber] = useState(null);
  const [isAccountManager, setIsAccountmanager] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUserData = useCallback(async () => {
    try {
      // console.log("Fetching user data...");
      const response = await axios.get("/api/v1/GetAppUserInformation/");
      const userInformation = normalizeUserInformation(response.data);
      const { id, firstname, lastname, userName, email, roles, allowedCompanies, isAccountManager } = userInformation;

      // console.log("User data fetched:", response.data);

      const companyNameResponse = await axios.get(`/api/v1/GetEntityNameByAppUserId/${id}`);
      const { name, entityId, entityNumber } = normalizeCompanyInformation(companyNameResponse.data);

      // console.log("Company name fetched:", companyNameResponse.data);

      setUserId(id);
      setUserName(userName);
      setDisplayName([firstname, lastname].filter(Boolean).join(" ") || userName || email);
      setEmail(email);
      setRoles(roles);
      setAllowedCompanies(allowedCompanies);
      setCompanyName(name);
      setCustomerId(entityId);
      setCustomerNumber(entityNumber);
      setIsAccountmanager(isAccountManager);
      const hasPortalUserRole = hasRequiredRole(roles, "PortalUser");
      // console.log("User has PortalUser role:", hasPortalUserRole);
      setIsAuthorized(hasPortalUserRole);

      if (!hasPortalUserRole) {
        console.warn("User does not have required PortalUser role. Available roles:", roles);
      }

      // console.log("State updated with fetched data.");
      return hasPortalUserRole;
    } catch (error) {
      console.error("Error fetching user info:", error);

      setIsAuthorized(false);

      return false;
    } finally {
      setIsLoading(false);
      // console.log("Loading state set to false");
    }
  }, []); // Empty dependency array ensures this function is stable and doesn't change on each render

  const updateCustomerIdAndNumber = useCallback(async (newCustomerId) => {
    // console.log("Updating customer ID and number..." + newCustomerId);
    try {
      const companyNameResponse = await axios.get(`/api/v1/GetEntityNameByEntityId/${newCustomerId}`);
      const { name, entityId, entityNumber } = normalizeCompanyInformation(companyNameResponse.data);
      setCustomerId(entityId);
      setCustomerNumber(entityNumber);
      setCompanyName(name);
      // console.log("State updated with fetched data.");
    } catch (error) {
      console.error("Error updating customer info:", error);
    }
  }, []); // Empty dependency array ensures this function is stable and doesn't change on each render

  return (
    <GlobalContext.Provider
      value={{
        userId,
        displayName,
        email,
        roles,
        allowedCompanies,
        userName,
        isAuthorized,
        companyName,
        customerId,
        customerNumber,
        isAccountManager,
        fetchUserData,
        updateCustomerIdAndNumber,
        isLoading,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export default GlobalProvider;
